import React from "react";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";
import { motion } from "framer-motion";

const ChatBubble = ({
  text,
  align = "left",
  color,
  borderColor,
  tailShape = "rounded",
  avatar,
  themeMood,
}) => {
  const isLeft = align === "left";
  const bubbleColor = color || (isLeft ? "#f3e6fa" : "#f9c6d1");
  const borderCol = borderColor || (isLeft ? "#e2cdb2" : "#f9c6d1");
  const shadowColor = isLeft
    ? "rgba(180,140,255,0.13)"
    : "rgba(255,140,180,0.13)";

  // Tail positioning
  const tailBase = 36; // px from left/right
  const tailWidth = tailShape === "rounded" ? 22 : 18;
  const tailHeight = tailShape === "rounded" ? 18 : 16;
  const tailBorderWidth = tailShape === "rounded" ? 26 : 20;
  const tailBorderHeight = tailShape === "rounded" ? 22 : 18;

  // For rounded tail, use border-radius on a small box overlay
  const roundedTail = (
    <Box
      sx={{
        position: "absolute",
        top: "100%",
        left: isLeft ? tailBase : "auto",
        right: isLeft ? "auto" : tailBase,
        width: 28,
        height: 18,
        bgcolor: bubbleColor,
        border: `2.5px solid ${borderCol}`,
        borderTop: "none",
        borderRadius: isLeft
          ? "0 0 18px 28px / 0 0 18px 28px"
          : "0 0 28px 18px / 0 0 28px 18px",
        zIndex: 1,
        boxShadow: `0 2px 8px ${shadowColor}`,
      }}
    />
  );

  // For angled tail, use triangles
  const angledTail = (
    <>
      {/* Border tail (behind) */}
      <Box
        sx={{
          position: "absolute",
          top: "100%",
          left: isLeft ? tailBase : "auto",
          right: isLeft ? "auto" : tailBase,
          width: 0,
          height: 0,
          borderStyle: "solid",
          borderWidth: `${tailBorderHeight}px ${
            isLeft ? tailBorderWidth : 0
          }px 0 ${isLeft ? 0 : tailBorderWidth}px`,
          borderColor: isLeft
            ? `${borderCol} transparent transparent transparent`
            : `${borderCol} transparent transparent transparent`,
          zIndex: 0,
        }}
      />
      {/* Fill tail (in front) */}
      <Box
        sx={{
          position: "absolute",
          top: "calc(100% - 2.5px)", // overlap border
          left: isLeft ? tailBase + 2 : "auto",
          right: isLeft ? "auto" : tailBase + 2,
          width: 0,
          height: 0,
          borderStyle: "solid",
          borderWidth: `${tailHeight}px ${isLeft ? tailWidth : 0}px 0 ${
            isLeft ? 0 : tailWidth
          }px`,
          borderColor: isLeft
            ? `${bubbleColor} transparent transparent transparent`
            : `${bubbleColor} transparent transparent transparent`,
          zIndex: 1,
        }}
      />
    </>
  );

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isLeft ? "flex-start" : "flex-end",
        width: "100%",
        mb: 2.2,
        position: "relative",
      }}
    >
      <motion.div
        whileHover={{ scale: 1.03, boxShadow: `0 6px 24px ${shadowColor}` }}
        whileTap={{ scale: 0.98 }}
        style={{ display: "inline-block", position: "relative", zIndex: 1 }}
      >
        <Box
          sx={{
            position: "relative",
            bgcolor: bubbleColor,
            color: "#2d223a",
            borderRadius: 5,
            boxShadow: `0 4px 18px ${shadowColor}, 0 1.5px 0.5px #e2cdb2`,
            px: 3.2,
            py: 2.1,
            fontSize: "1.13rem",
            fontWeight: 500,
            minWidth: 120,
            maxWidth: "92vw",
            wordBreak: "break-word",
            border: `2.5px solid ${borderCol}`,
            width: "fit-content",
            ml: isLeft ? 0 : "auto",
            mr: isLeft ? "auto" : 0,
            transition: "box-shadow 0.18s, background 0.18s, border 0.18s",
            filter: "drop-shadow(0 2px 6px rgba(124,92,255,0.07))",
            animation: "bubblePop 0.7s cubic-bezier(.68,-0.55,.27,1.55)",
            "@keyframes bubblePop": {
              "0%": { transform: "scale(0.92)" },
              "60%": { transform: "scale(1.06)" },
              "100%": { transform: "scale(1)" },
            },
          }}
        >
          {/* Tail shape */}
          {tailShape === "rounded" ? roundedTail : angledTail}
          {/* Avatar emoji */}
          {avatar && (
            <Box
              sx={{
                position: "absolute",
                top: -28,
                left: isLeft ? 12 : "auto",
                right: isLeft ? "auto" : 12,
                fontSize: "1.5rem",
                zIndex: 2,
                filter: "drop-shadow(0 2px 6px rgba(124,92,255,0.13))",
                background: "rgba(255,255,255,0.7)",
                borderRadius: "50%",
                px: 0.5,
                py: 0.2,
                border: `1.5px solid ${borderCol}`,
              }}
            >
              {avatar}
            </Box>
          )}
          {text}
        </Box>
      </motion.div>
    </Box>
  );
};

ChatBubble.propTypes = {
  text: PropTypes.string.isRequired,
  align: PropTypes.oneOf(["left", "right"]),
  color: PropTypes.string,
  borderColor: PropTypes.string,
  tailShape: PropTypes.oneOf(["rounded", "angled"]),
  avatar: PropTypes.string,
  themeMood: PropTypes.object,
};

export default ChatBubble;
